import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { categoriesForRubro, normalizeSearch, uuidv7, validateCategoryName } from '@pos/core';
import { RUBROS, applyLocalChanges, type CategoryRow, type LocalChange } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, Input, PageHeader, Spinner, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { buildCategoryDeleteChanges } from './changes.ts';
import { useCatalog } from './data.ts';

export function CategoriesPage() {
  const { business, writer } = useSession();
  const catalog = useCatalog();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [toDelete, setToDelete] = useState<CategoryRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!catalog) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  const counts = new Map<string, number>();
  for (const i of catalog.items) if (i.category) counts.set(i.category.id, (counts.get(i.category.id) ?? 0) + 1);
  const taken = (value: string, exceptId?: string) =>
    catalog.categories.some((c) => c.id !== exceptId && normalizeSearch(c.name) === normalizeSearch(value));

  const run = async (changes: LocalChange[]) => {
    setBusy(true);
    setError(null);
    try {
      await applyLocalChanges(db, changes, writer);
      requestSync();
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const newCategory = (n: string): LocalChange => {
    const now = new Date().toISOString();
    return {
      table: 'categories',
      kind: 'insert',
      row: { id: uuidv7(), business_id: business.id, name: n.trim(), sort: 0, deleted_at: null, created_at: now, updated_at: now },
    };
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const problem = validateCategoryName(name) ?? (taken(name) ? 'Ya existe una categoría con ese nombre.' : null);
    if (problem) return setError(problem);
    if (await run([newCategory(name)])) setName('');
  };

  const rename = async () => {
    if (!editing) return;
    const problem = validateCategoryName(editing.name) ?? (taken(editing.name, editing.id) ? 'Ya existe una categoría con ese nombre.' : null);
    if (problem) return setError(problem);
    if (await run([{ table: 'categories', kind: 'patch', id: editing.id, changes: { name: editing.name.trim() } }])) setEditing(null);
  };

  const suggested = categoriesForRubro(business.rubro).filter((n) => !taken(n));

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader
        title="Categorías"
        description="Agrupan tus productos para buscarlos y para hacer aumentos por categoría."
        actions={
          <Link to="/productos">
            <Button variant="secondary">Volver</Button>
          </Link>
        }
      />

      <Card>
        <form className="flex gap-2" onSubmit={add}>
          <Input placeholder="Nueva categoría" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} aria-label="Nombre de la categoría" />
          <Button type="submit" loading={busy}>
            Agregar
          </Button>
        </form>
        {suggested.length > 0 && (
          <div className="mt-3 text-sm text-slate-600">
            Sugeridas para {RUBROS[business.rubro].toLowerCase()}: {suggested.join(', ')}.{' '}
            <button type="button" className="font-medium text-brand hover:underline" disabled={busy} onClick={() => run(suggested.map(newCategory))}>
              Agregarlas todas
            </button>
          </div>
        )}
      </Card>

      {error && <Alert tone="error">{error}</Alert>}

      <Card flush className="divide-y divide-slate-100">
        {catalog.categories.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Todavía no hay categorías.</p>}
        {catalog.categories.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-3 sm:px-4">
            {editing?.id === c.id ? (
              <>
                <Input value={editing.name} maxLength={60} autoFocus onChange={(e) => setEditing({ id: c.id, name: e.target.value })} aria-label="Nuevo nombre" />
                <Button onClick={rename} loading={busy}>
                  Guardar
                </Button>
                <Button variant="ghost" onClick={() => setEditing(null)}>
                  Cancelar
                </Button>
              </>
            ) : (
              <>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{c.name}</p>
                  <p className="text-sm text-slate-500">{counts.get(c.id) ?? 0} productos</p>
                </div>
                <Button variant="ghost" onClick={() => setEditing({ id: c.id, name: c.name })}>
                  Renombrar
                </Button>
                <Button variant="dangerGhost" onClick={() => setToDelete(c)}>
                  Eliminar
                </Button>
              </>
            )}
          </div>
        ))}
      </Card>

      <Dialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        title={`¿Eliminar "${toDelete?.name}"?`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setToDelete(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={async () => {
                if (toDelete && (await run(buildCategoryDeleteChanges(toDelete.id, catalog.items.map((i) => i.product), new Date().toISOString())))) {
                  setToDelete(null);
                }
              }}
            >
              Eliminar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          {toDelete && (counts.get(toDelete.id) ?? 0) > 0
            ? `Sus ${counts.get(toDelete.id)} productos quedan sin categoría (no se borran).`
            : 'No tiene productos.'}
        </p>
      </Dialog>
    </div>
  );
}
